import { useCloudApi } from '@/api/cloudApi'
import { useAnalytics } from '@/composables/useAnalytics'
import type {
  V1alpha1KafkaCluster,
  V1ObjectMeta,
  CloudV1alpha1Condition,
  V1alpha1SnSchemaRegistry
} from '@streamnative/cloud-api-client-typescript'
import { i18n } from '@/lang'
import type { ComputedRef, Ref } from 'vue'
import AsyncLock from 'async-lock'
import { useIntervalFn } from '@vueuse/core'
import { getErrorMessage } from '@/utils/apiHelper'
import type { PulsarState } from './usePulsarState'
import { rbacHelper } from '@/utils/rbacHelper'
import { rbacManager } from '@/utils/rbacManager'
import {
  buildKafkaClusterPayload,
  type KafkaClusterResourcePayload
} from '@/utils/kafkaClusterPayload'

export type { KafkaClusterResourcePayload } from '@/utils/kafkaClusterPayload'

let lastOrg: string | undefined = undefined

const { t } = i18n.global
const lock = new AsyncLock()

// Kafka Cluster interfaces
export interface KafkaCluster extends V1alpha1KafkaCluster {
  metadata: V1ObjectMeta & {
    name: string
    namespace: string
  }
  generated: {
    conditions: KafkaClusterConditions
    serviceEndpoints: KafkaServiceEndpoint[]
    deleted: boolean
    version?: string
  }
}

export interface KafkaClusterConditions {
  Ready: boolean
  Available: boolean
}

export interface KafkaServiceEndpoint {
  access?: string
  webServiceUrl?: string // 'https://${dnsName}'
  kafkaRestProxyUrl?: string // service endpoint from status.kafkaRestProxy.serviceEndpoint
  serviceEndpoint?: string // service endpoint from status.serviceEndpoints.find(endpoint => endpoint.type === 'external')
  schemaRegistryUrl?: string // service endpoint from status.serviceEndpoints.find(endpoint => endpoint.type === 'external')
}

// State
const kafkaClusterMap: Ref<Record<string, Array<KafkaCluster>>> = ref({})
const schemaRegistryMap: Ref<Record<string, V1alpha1SnSchemaRegistry>> = ref({})

export const isLoadingKafkaClusters = ref(false)

const getSchemaRegistryByClusterId = (clusterId: string) => {
  return schemaRegistryMap.value[clusterId]
}

const getSchemaRegistryUidByClusterId = (clusterId: string) => {
  return schemaRegistryMap.value[clusterId]?.metadata?.uid
}

const getSchemaRegistryServiceEndpointByClusterId = (clusterId: string) => {
  return schemaRegistryMap.value[clusterId]?.status?.serviceEndpoints?.find(
    endpoint => endpoint.type === 'external'
  )?.dnsName
}

// Helper functions
const mustGetSelectedInstance = (): string => {
  return usePulsarState().mustInstance()
}

// Computed properties
const kafkaClusters: ComputedRef<Array<KafkaCluster>> = computed(() => {
  const { instance } = usePulsarState()
  if (!instance.value || !kafkaClusterMap.value[instance.value]) {
    return []
  }
  return kafkaClusterMap.value[instance.value].sort((a, b) => {
    return a.metadata.name.localeCompare(b.metadata.name)
  })
})

const kafkaClustersFn = (instance: string) => {
  if (!instance || !kafkaClusterMap.value[instance]) {
    return []
  }
  return kafkaClusterMap.value[instance].sort((a, b) => {
    return a.metadata.name.localeCompare(b.metadata.name)
  })
}

const kafkaClusterNames: ComputedRef<Array<string>> = computed(() => {
  const { instance } = usePulsarState()
  if (!instance.value || !kafkaClusterMap.value[instance.value]) {
    return []
  }
  return kafkaClusterMap.value[instance.value].map(cluster => cluster.metadata.name).sort()
})

const activeKafkaCluster: ComputedRef<KafkaCluster | undefined> = computed(() => {
  const { instance, clusterUid } = usePulsarState()
  if (!instance.value) {
    return undefined
  }

  if (!kafkaClusterMap.value[instance.value]?.values) {
    return undefined
  }

  const targetCluster = kafkaClusterMap.value[instance.value]?.find(cluster => {
    return cluster.metadata?.uid === clusterUid.value
  })

  if (targetCluster) {
    return targetCluster
  }

  return undefined
})

const activeKafkaClusterName = computed(() => activeKafkaCluster.value?.metadata?.name)

// Cluster status helpers
const isKafkaClusterReadyFn = (cluster: KafkaCluster | undefined) => {
  if (!cluster) {
    return false
  }
  return cluster.generated.conditions.Ready
}

const isKafkaClusterAvailableFn = (cluster: KafkaCluster | undefined) => {
  if (!cluster) {
    return false
  }
  return isKafkaClusterReadyFn(cluster) || cluster.generated.conditions.Available
}

const isKafkaClusterReadyByNameFn = (name: string, instance: string) => {
  const cluster = kafkaClusterMap.value[instance]?.find(item => item.metadata.name === name)
  return isKafkaClusterReadyFn(cluster)
}

const isKafkaClusterAvailableByNameFn = (name: string, instance: string) => {
  const cluster = kafkaClusterMap.value[instance]?.find(item => item.metadata.name === name)
  return isKafkaClusterAvailableFn(cluster)
}

const isActiveKafkaClusterReady: ComputedRef<boolean> = computed(() => {
  return isKafkaClusterReadyFn(activeKafkaCluster.value)
})

const isActiveKafkaClusterAvailable: ComputedRef<boolean> = computed(() => {
  return isKafkaClusterAvailableFn(activeKafkaCluster.value)
})

// Mutations
const setKafkaCluster = (
  cluster: KafkaCluster,
  instanceName: string = mustGetSelectedInstance()
) => {
  const clusterIndex = kafkaClusterMap.value[instanceName]
    ? kafkaClusterMap.value[instanceName].findIndex(
        item => item.metadata.name === cluster.metadata.name
      )
    : -1
  if (clusterIndex > -1) {
    kafkaClusterMap.value[instanceName].splice(clusterIndex, 1, cluster)
  } else {
    if (!kafkaClusterMap.value[instanceName]) {
      kafkaClusterMap.value[instanceName] = []
    }
    kafkaClusterMap.value[instanceName].push(cluster)
  }
}

const setActiveKafkaClusterDeleted = (cluster: KafkaCluster) => {
  cluster.generated.deleted = true
}

const isKafkaClusterDeletedFn = (cluster: KafkaCluster | undefined) => {
  if (!cluster) {
    return false
  }
  return cluster.metadata.deletionTimestamp || cluster.generated.deleted === true
}

const isKafkaClusterDeletedByNameFn = (name: string, instance: string) => {
  const cluster = kafkaClusterMap.value[instance]?.find(item => item.metadata.name === name)
  return isKafkaClusterDeletedFn(cluster)
}

// API operations
const createKafkaCluster = async (payload: KafkaClusterResourcePayload): Promise<KafkaCluster> => {
  try {
    if (!payload.name) {
      throw Error(t('cluster.clusterNameIsRequired'))
    }

    if (!payload.location) {
      throw Error(t('cluster.clusterLocationIsRequired'))
    }

    const body = buildKafkaClusterPayload(payload)
    const newCluster = formatKafkaClusterModel(
      (await useCloudApi().createNamespacedKafkaCluster(payload.organization, body)).data
    )
    setKafkaCluster(newCluster, payload.instance)
    useAnalytics().track('kafka-cluster/create/success', {
      organization: payload.organization,
      instance: payload.instance,
      name: payload.name,
      location: payload.location,
      throughputUnit: payload.throughputUnit
    })
    return newCluster
  } catch (e) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: Object is of type 'unknown'
    if (e && e.response && e.response.status === 409) {
      throw Error(t('cluster.duplicateClusterError'))
    }
    throw Error(getErrorMessage(e))
  }
}

const updateKafkaCluster = async (payload: KafkaClusterResourcePayload): Promise<KafkaCluster> => {
  const patchArray: Record<string, unknown>[] = []
  const clusterObj = kafkaClusterMap.value[payload.instance]?.find(
    c => c.metadata.name === payload.name
  )
  const clusterConfig = clusterObj?.spec?.config as
    | {
        lakehouseStorage?: {
          enabled?: boolean
        }
      }
    | undefined

  if (!clusterObj) {
    throw Error('Kafka cluster to update is not found')
  }

  // Update throughput units
  if (payload.throughputUnit !== undefined) {
    patchArray.push({
      op: 'replace',
      path: '/spec/throughputUnit',
      value: payload.throughputUnit
    })
  }

  // Update display name
  if (payload.displayName !== undefined) {
    patchArray.push({
      op: 'replace',
      path: '/spec/displayName',
      value: payload.displayName
    })
  }

  // Update catalogs
  if (payload.catalogs !== undefined) {
    if (payload.catalogs.length > 0) {
      patchArray.push({
        op: clusterObj.spec?.catalogs ? 'replace' : 'add',
        path: '/spec/catalogs',
        value: payload.catalogs
      })
    }
  }

  if (payload.config?.lakehouseStorage !== undefined) {
    if (clusterConfig?.lakehouseStorage) {
      patchArray.push({
        op: 'replace',
        path: '/spec/config/lakehouseStorage',
        value: payload.config.lakehouseStorage
      })
    } else if (clusterConfig) {
      patchArray.push({
        op: 'add',
        path: '/spec/config/lakehouseStorage',
        value: payload.config.lakehouseStorage
      })
    } else {
      patchArray.push({
        op: 'add',
        path: '/spec/config',
        value: {
          lakehouseStorage: payload.config.lakehouseStorage
        }
      })
    }
  }

  try {
    const { data } = await useCloudApi().patchNamespacedKafkaCluster(
      payload.name,
      payload.organization,
      patchArray
    )
    const clusterModel = formatKafkaClusterModel(data)
    setKafkaCluster(clusterModel, payload.instance)
    useAnalytics().track('kafka-cluster/update/success', {
      organization: payload.organization,
      instance: payload.instance,
      name: payload.name
    })
    return clusterModel
  } catch (e) {
    throw Error(getErrorMessage(e))
  }
}

const deleteKafkaCluster = async (cluster: KafkaCluster) => {
  try {
    await useCloudApi().deleteNamespacedKafkaCluster(
      cluster.metadata.name,
      cluster.metadata.namespace
    )
    setActiveKafkaClusterDeleted(cluster)
    useAnalytics().track('kafka-cluster/delete/success', {
      organization: cluster.metadata.namespace,
      name: cluster.metadata.name
    })
  } catch (error) {
    throw Error(getErrorMessage(error, 'Failed to delete the Kafka cluster'))
  }
}

const deleteKafkaClusterByName = async (name: string, instance: string) => {
  const cluster = kafkaClusterMap.value[instance]?.find(item => item.metadata.name === name)
  if (cluster) {
    await deleteKafkaCluster(cluster)
  }
}

// Fetch operations
const getKafkaClusterMap = async ({ organization }: { organization: string }) => {
  const cm = await getKafkaClusterMapRaw({ organization })
  kafkaClusterMap.value = cm
}

const getKafkaClusterMapRaw = async ({ organization }: { organization: string }) => {
  const { isKafkaEnabled } = useOrganization()
  if (!isKafkaEnabled.value) {
    return {}
  }
  isLoadingKafkaClusters.value = true
  try {
    const { data } = await useCloudApi().listNamespacedKafkaCluster(organization)
    const schemaRegistryList = await getSchemaRegistryList(organization)

    const cm: Record<string, Array<KafkaCluster>> = {}
    data.items?.forEach(cluster => {
      if (!cluster.spec?.instanceName) {
        console.warn(`Invalid instance name is passed in: ${cluster}`)
        return
      }
      const instanceName = cluster.spec?.instanceName
      const schemaRegistry = schemaRegistryList.find(
        item => item.metadata?.name === cluster.metadata?.name
      )
      if (schemaRegistry && schemaRegistry.metadata?.uid && cluster.metadata?.uid) {
        schemaRegistryMap.value[cluster.metadata?.uid] = schemaRegistry as V1alpha1SnSchemaRegistry
      }
      if (!cm[instanceName]) {
        cm[instanceName] = []
      }

      cm[instanceName].push(formatKafkaClusterModel(cluster, schemaRegistry))
    })

    return cm
  } catch (e) {
    throw Error(getErrorMessage(e))
  } finally {
    isLoadingKafkaClusters.value = false
  }
}

const readKafkaCluster = async (
  name: string,
  organization: string
): Promise<KafkaCluster | undefined> => {
  try {
    const { data } = await useCloudApi().readNamespacedKafkaCluster(name, organization)
    return formatKafkaClusterModel(data)
  } catch (e) {
    console.error('Failed to read Kafka cluster:', e)
    return undefined
  }
}

// Format cluster model
export const formatKafkaClusterModel = (
  cluster: V1alpha1KafkaCluster,
  schemaRegistry?: V1alpha1SnSchemaRegistry
): KafkaCluster => {
  if (!cluster.metadata?.uid) {
    throw new Error('Kafka cluster is missing UID')
  }

  const conditions = (cluster.status?.conditions?.reduce(
    (curr: Record<string, boolean> = {}, condition: CloudV1alpha1Condition) => {
      curr[condition.type] = condition.status === 'True'
      return curr
    },
    {}
  ) ?? {
    Ready: false,
    Available: false
  }) as unknown as KafkaClusterConditions

  // Compute availability based on component status
  conditions.Ready =
    cluster.status?.conditions?.find(c => c.type === 'Ready')?.status === 'True' || false
  conditions.Available = conditions.Ready

  const kafkaRestProxyServiceEndpoint = cluster.status?.kafkaRestProxy?.serviceEndpoint

  /**
   * status:
  conditions:
  - lastTransitionTime: "2026-01-23T16:46:37Z"
    reason: AllConditionStatusTrue
    status: "True"
    type: Ready
  serviceEndpoints:
  - dnsName: my-kafka-schema-registry.max.svc.cluster.local:8081
    type: internal
  - dnsName: my-kafka-schema-registry.gcp-usw1-test-jyr2m.test.g.sn2.dev
    type: external
   */
  const schemaRegistryServiceEndpoint = cluster.status?.serviceEndpoints?.find(
    endpoint => endpoint.type === 'external'
  )?.dnsName

  const schemaRegistryDnsName = schemaRegistry?.status?.serviceEndpoints?.find(
    endpoint => endpoint.type === 'external'
  )?.dnsName

  const serviceEndpoints: KafkaServiceEndpoint[] = [
    {
      access: 'Public',
      webServiceUrl: kafkaRestProxyServiceEndpoint
        ? `https://${kafkaRestProxyServiceEndpoint}`
        : undefined,
      kafkaRestProxyUrl: kafkaRestProxyServiceEndpoint
        ? `https://${kafkaRestProxyServiceEndpoint}`
        : undefined,
      serviceEndpoint: schemaRegistryServiceEndpoint
        ? `${schemaRegistryServiceEndpoint}`
        : undefined,
      schemaRegistryUrl: schemaRegistryDnsName ? `https://${schemaRegistryDnsName}` : undefined
    }
  ]

  return {
    ...cluster,
    metadata: {
      ...cluster.metadata,
      name: cluster.metadata.name as string,
      namespace: cluster.metadata.namespace as string
    },
    generated: {
      conditions,
      serviceEndpoints,
      deleted: !!cluster.metadata?.deletionTimestamp,
      version: cluster.status?.image?.split(':')[1]
    }
  }
}

// Ping cluster for status updates
export const usePingKafkaCluster = () => {
  const cluster = ref<V1alpha1KafkaCluster>()
  const conditions = computed(() => cluster.value?.status?.conditions ?? [])
  const error = ref('')

  const { resume, pause, isActive } = useIntervalFn(async () => {
    if (!activeKafkaCluster.value) {
      return
    }
    const clusterName = activeKafkaCluster.value.metadata.name
    const organization = activeKafkaCluster.value.metadata.namespace
    const clusterUid = activeKafkaCluster.value.metadata.uid || ''

    if (!lock.isBusy(clusterUid)) {
      await lock.acquire(clusterUid, async () => {
        try {
          const { data } = await useCloudApi().readNamespacedKafkaCluster(clusterName, organization)
          cluster.value = data
          const formattedCluster = formatKafkaClusterModel(data)
          setKafkaCluster(formattedCluster, formattedCluster.spec?.instanceName)
        } catch (e) {
          error.value = getErrorMessage(e)
          pause()
        }
      })
    }
  }, 5000)

  return {
    cluster,
    conditions,
    resume,
    pause,
    isActive,
    error
  }
}

// Helper functions
const getKafkaClusterFromUid = (uid: string) => {
  let cluster = undefined
  for (const instanceName in kafkaClusterMap.value) {
    cluster = kafkaClusterMap.value[instanceName].find(c => c.metadata.uid === uid)
    if (cluster) {
      break
    }
  }
  return cluster
}

const getKafkaClusterUidFromName = (name: string) => {
  let cluster = undefined

  for (const instanceName in kafkaClusterMap.value) {
    cluster = kafkaClusterMap.value[instanceName].find(c => c.metadata.name === name)
    if (cluster) {
      break
    }
  }
  return cluster?.metadata.uid
}

const kafkaClusterTitleFn = (cluster?: V1alpha1KafkaCluster) => {
  if (cluster?.spec?.displayName && cluster?.spec?.displayName !== cluster?.metadata?.name) {
    return `${cluster?.spec?.displayName} (${cluster?.metadata?.name})`
  }
  return `${cluster?.metadata?.name}`
}

const activeKafkaClusterTitle = computed(() => {
  return kafkaClusterTitleFn(activeKafkaCluster.value)
})

const viewableKafkaClustersFn = (
  instance: string
): {
  label: string
  value: string
  id: string
  name: string
  location: string
  disabled: boolean
}[] => {
  if (instance === undefined || !kafkaClusterMap.value[instance]) {
    return []
  }

  const clusterOptions = (kafkaClusterMap.value[instance] || [])
    .map(cluster => {
      return {
        label: kafkaClusterTitleFn(cluster) || '',
        value: cluster?.metadata?.name || '',
        id: cluster?.metadata?.uid || '',
        name: cluster?.metadata?.name || '',
        location: cluster?.spec?.location || '',
        disabled: false
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label))
  return clusterOptions
}

const viewableKafkaClusters = computed<{ label: string; value: string; disabled: boolean }[]>(
  () => {
    const { instance } = usePulsarState()
    if (instance.value === undefined) {
      return []
    }
    return viewableKafkaClustersFn(instance.value)
  }
)

// Find the instanceName from kafkaClusterMap based on clusterName
const getInstanceByKafkaClusterName = (clusterName: string) => {
  return Object.keys(kafkaClusterMap.value).find(instance =>
    kafkaClusterMap.value[instance].some(c => c.metadata.name === clusterName)
  )
}

const getKafkaClusterDisplayNameByName = (clusterName: string) => {
  const instanceName = getInstanceByKafkaClusterName(clusterName)

  if (instanceName) {
    const cluster = kafkaClusterMap.value[instanceName]?.find(c => c.metadata?.name === clusterName)
    return cluster?.spec?.displayName || clusterName
  }
  return ''
}

const getSchemaRegistryList = async (organization: string) => {
  try {
    const { data } = await useCloudApi().listNamespacedSnSchemaRegistry(organization)
    return data.items ?? []
  } catch (error) {
    console.error('Failed to get schema registry list:', error)
    return []
  }
}

// Initialization
export const init = (initialState: PulsarState) => {
  const { organization } = usePulsarState()
  const { isRbacUpdating } = rbacHelper()

  const valueChanged = async ([org, ab]: [string | undefined, boolean | undefined]) => {
    if (!org) {
      kafkaClusterMap.value = {}
      lastOrg = undefined
      return
    }
    if (ab) {
      return
    }

    if (lastOrg !== org) {
      const { canDescribeClusterList } = rbacManager()
      if (canDescribeClusterList()) {
        await getKafkaClusterMap({ organization: org })
      }
    }

    lastOrg = org
  }

  watch([organization, isRbacUpdating], valueChanged)

  return valueChanged([initialState.organization, isRbacUpdating.value])
}

// Export composable
export const useKafkaCluster = () => {
  return {
    // State
    kafkaClusterMap,
    kafkaClusters,
    kafkaClusterNames,
    activeKafkaCluster,
    activeKafkaClusterName,
    isLoadingKafkaClusters,

    // Status helpers
    isKafkaClusterReadyFn,
    isKafkaClusterAvailableFn,
    isKafkaClusterReadyByNameFn,
    isKafkaClusterAvailableByNameFn,
    isActiveKafkaClusterReady,
    isActiveKafkaClusterAvailable,
    isKafkaClusterDeletedFn,
    isKafkaClusterDeletedByNameFn,

    // CRUD operations
    createKafkaCluster,
    updateKafkaCluster,
    deleteKafkaCluster,
    deleteKafkaClusterByName,
    readKafkaCluster,

    // Fetch operations
    getKafkaClusterMap,
    getKafkaClusterMapRaw,
    kafkaClustersFn,

    // Helpers
    getKafkaClusterFromUid,
    getKafkaClusterUidFromName,
    kafkaClusterTitleFn,
    activeKafkaClusterTitle,
    viewableKafkaClusters,
    viewableKafkaClustersFn,
    getInstanceByKafkaClusterName,
    getKafkaClusterDisplayNameByName,

    // Format
    formatKafkaClusterModel,

    // Schema Registry
    getSchemaRegistryUidByClusterId,
    getSchemaRegistryByClusterId,
    getSchemaRegistryServiceEndpointByClusterId,

    // Init
    init
  }
}
